import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useRequest } from 'ahooks';
import { useField, useForm } from 'react-final-form-hooks';
import {
  Button,
  ConfigProvider,
  Form,
  Input,
  Modal,
  notification,
  Select,
  StatusTip,
} from 'tea-component';
import Loading from '@/pages/datalab/components/Loading';
import { showErrorMessagetip } from '@/pages/home/components/ErrorMessageTip/ErrorMessageTip';
import { createChartTag, getChartTagList } from '@dbl/services/chart/chartTag';
import { getStatus } from '@/pages/datalab/pages/Multidimensional/Analysis/components/SaveModel';
import { checkNullString, translateSpecialWord, useDblIntl } from '@/pages/datalab/utils/utils';
import { copyChart } from '../../service';
import '@/pages/datalab/pages/Multidimensional/Analysis/components/SaveModel/index.scss';

/** 标签下拉选项（含英文展示字段，与 SaveModel 保持一致） */
interface ILabelSelectOption {
  value: string;
  text: string;
  text_en: string;
}

/**
 * 将用户标签接口数据转为 Select 选项。
 * 复制场景需预填源图表 label，若该标签不在当前用户列表中也要能展示/选中，故合并 sourceLabel。
 */
const buildLabelOptions = (labels: string[], sourceLabel?: string): ILabelSelectOption[] => {
  const labelSet = new Set(labels);
  const mergedLabels =
    sourceLabel && !labelSet.has(sourceLabel) ? [sourceLabel, ...labels] : labels;
  return mergedLabels.map((item) => ({
    value: item,
    text: item,
    text_en: translateSpecialWord(item, true),
  }));
};

export interface ICopyChartRecord {
  task_id: string | number;
  name: string;
  name_cn?: string;
  label?: string;
}

interface ICopyChartModalProps {
  visible: boolean;
  gameCode: string;
  record?: ICopyChartRecord;
  onClose: () => void;
  onSuccess: () => void;
}

interface ICopyChartFormValues {
  name: string;
  nameCn: string;
  label: string;
}

/**
 * 图表中心「复制图表」弹窗。
 * 调用 createChart 后端复制模式（传 old_id，不传 task_id / filter），表单字段与 SaveModel 对齐。
 */
const CopyChartModal: React.FC<ICopyChartModalProps> = ({
  visible,
  gameCode,
  record,
  onClose,
  onSuccess,
}) => {
  const { dblFormatMessage: myAnalysisFormatMessage, commonFormatMessage, isEn } =
    useDblIntl('myAnalysis');
  const { dblFormatMessage: multiFormatMessage } = useDblIntl('multi');
  /** true：接口返回 21101 英文名重名，需在表单项上展示专用错误文案 */
  const [duplicateName, setDuplicateName] = useState<boolean>(false);
  /** 标签下拉加载态，与 SaveModel status 语义一致：'' | loading | empty | error */
  const [labelStatus, setLabelStatus] = useState<'' | 'loading' | 'empty' | 'error'>('');
  const [labelOptions, setLabelOptions] = useState<ILabelSelectOption[]>([]);
  /** true：Select 底部切换为自定义标签输入区，此时关闭 searchable 避免交互冲突 */
  const [showCustomLabelInput, setShowCustomLabelInput] = useState<boolean>(false);
  const [customLabelInputValue, setCustomLabelInputValue] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);

  const { form, handleSubmit, invalid, validating } = useForm({
    onSubmit: async (values: ICopyChartFormValues) => {
      if (!record?.task_id) {
        return;
      }
      setSubmitting(true);
      try {
        // 后端复制模式：old_id + name/game_code/create_time 必填；name_cn/label 传入则覆盖源图表
        const resp: any = await copyChart({
          old_id: String(record.task_id),
          name: values.name?.trim(),
          name_cn: values.nameCn?.trim() || undefined,
          label: values.label,
          game_code: gameCode,
          create_time: moment().format('YYYY-MM-DD HH:mm:ss'),
        });
        if (resp?.code === 21101) {
          setDuplicateName(true);
          return;
        }
        if (resp?.code === 0) {
          notification.success({
            description: myAnalysisFormatMessage('copy_success'),
          });
          onSuccess();
          onClose();
          return;
        }
        notification.error({
          description: myAnalysisFormatMessage('copy_fail', { code: resp?.code ?? -1 }),
        });
      } catch (error) {
        notification.error({
          description: commonFormatMessage('copy_error'),
        });
      } finally {
        setSubmitting(false);
      }
    },
    initialValuesEqual: () => true,
    // 校验规则与 Multidimensional SaveModel 保持一致（英文名必填，中文名选填，标签必填，最长 50 字符）
    validate: (values: ICopyChartFormValues) => {
      const { name, nameCn, label } = values;
      const getValidateNameText = (value: string, langKey: string) => {
        if (!value?.replaceAll(' ', '')) {
          return multiFormatMessage(langKey);
        }
        if (value?.length > 50) {
          return commonFormatMessage('valid_name_length', { count: '50' });
        }
        return undefined;
      };

      return {
        name: getValidateNameText(name, 'task_name_validate'),
        nameCn:
          nameCn?.length > 50
            ? commonFormatMessage('valid_name_length', { count: '50' })
            : undefined,
        label: !label ? multiFormatMessage('task_label_validate') : undefined,
      };
    },
  });

  const name = useField('name', form);
  const nameCn = useField('nameCn', form);
  const label = useField('label', form);

  const { run: runGetChartTagList } = useRequest(
    () => {
      setLabelStatus('loading');
      return getChartTagList({ game_code: gameCode });
    },
    {
      manual: true,
      onSuccess: (res: any) => {
        // 接口返回后再次合并 sourceLabel，避免异步覆盖 form.initialize 阶段注入的源标签
        const newOptions = buildLabelOptions(res?.labels || [], record?.label);
        setLabelOptions(newOptions);
        setLabelStatus(newOptions.length === 0 ? 'empty' : '');
      },
      onError: () => {
        setLabelStatus('error');
      },
    },
  );

  const { run: runCreateChartTag, loading: createChartTagLoading } = useRequest(
    (labelValue: string) => createChartTag({ game_code: gameCode, label: labelValue }),
    {
      manual: true,
      onSuccess: (res: any, params) => {
        const addedLabel = params?.[0];
        const newOptions = buildLabelOptions(res?.labels || [], record?.label);
        setLabelOptions(newOptions);
        // 优先选中用户刚创建的标签，与 SaveModel 自定义标签流程对齐
        form.change('label', addedLabel || (newOptions?.[0]?.value ?? ''));
        setLabelStatus(newOptions.length === 0 ? 'empty' : '');
        setShowCustomLabelInput(false);
      },
      onError: (res: any) => {
        // 21110：标签名与已有标签重复
        if (res?.code === 21110) {
          showErrorMessagetip({
            errorCode: 21110,
            content: multiFormatMessage('task_label_same'),
            duration: 3000,
            isClose: true,
            type: 'error',
          });
        }
      },
    },
  );

  // 弹窗开关：打开时拉标签列表，关闭时清空本地态，避免下次打开残留
  useEffect(() => {
    if (!visible) {
      setDuplicateName(false);
      setShowCustomLabelInput(false);
      setCustomLabelInputValue('');
      setLabelOptions([]);
      setLabelStatus('');
      return;
    }
    runGetChartTagList();
  }, [visible, gameCode, runGetChartTagList]);

  // 源图表变更时重置表单默认值；名称追加「(复制)」后缀便于区分
  useEffect(() => {
    if (!visible || !record) {
      return;
    }
    const copySuffix = multiFormatMessage('copy');
    form.initialize({
      name: !checkNullString(record.name) ? `${record.name}${copySuffix}` : '',
      nameCn: !checkNullString(record.name_cn) ? `${record.name_cn}${copySuffix}` : '',
      label: record.label || '',
    });
    // 与 SaveModel 一致：源图表标签不在用户标签列表时，先写入 options 以便 Select 能展示
    const chartTag = record.label;
    if (chartTag) {
      setLabelOptions((prev) => {
        const isHave = prev.some(({ value }) => value === chartTag);
        if (isHave) {
          return prev;
        }
        return [
          {
            value: chartTag,
            text: chartTag,
            text_en: translateSpecialWord(chartTag, true),
          },
          ...prev,
        ];
      });
    }
  }, [visible, JSON.stringify(record), form, multiFormatMessage]);

  // 退出自定义标签输入模式时清空草稿，避免再次展开时残留
  useEffect(() => {
    if (!showCustomLabelInput) {
      setCustomLabelInputValue('');
    }
  }, [showCustomLabelInput]);

  if (!visible) {
    return null;
  }

  /** 与 SaveModel 相同：有源 taskId 时才把 label 绑定到 Select，否则仅显示 placeholder */
  const originTaskId = record?.task_id;

  return (
    <Modal
      className="dbl-analysis-save-model"
      visible={visible}
      caption={myAnalysisFormatMessage('copy_chart_title')}
      onClose={onClose}
    >
      <Loading loading={createChartTagLoading}>
        <ConfigProvider locale={isEn ? 'en' : 'zh'}>
          <form
            onSubmit={handleSubmit}
            onKeyDown={(e) => {
              // 与 SaveModel 一致：Enter 不触发提交，避免误操作
              if (e.key === 'Enter') {
                e.preventDefault();
              }
            }}
          >
            <Modal.Body className="dbl-confirm-model dbl-analysize-confirm-model-box">
              <Form>
                <Form.Item
                  label={multiFormatMessage('task_name')}
                  status={duplicateName ? 'error' : getStatus(name.meta, validating)}
                  showStatusIcon={false}
                  required
                  message={
                    duplicateName
                      ? multiFormatMessage('task_name_same')
                      : getStatus(name.meta, validating) === 'error' && name.meta.error
                  }
                >
                  <Input
                    placeholder={multiFormatMessage('task_name_placeholder')}
                    size="full"
                    autoComplete="off"
                    {...name.input}
                  />
                </Form.Item>
                <Form.Item
                  label={multiFormatMessage('task_name_cn')}
                  showStatusIcon={false}
                  status={getStatus(nameCn.meta, validating)}
                  message={
                    getStatus(nameCn.meta, validating) === 'error' && nameCn.meta.error
                  }
                >
                  <Input
                    placeholder={multiFormatMessage('task_name_cn_placeholder')}
                    size="full"
                    autoComplete="off"
                    {...nameCn.input}
                  />
                </Form.Item>
                <Form.Item
                  label={multiFormatMessage('task_label')}
                  showStatusIcon={false}
                  required
                  status={getStatus(label.meta, validating)}
                  message={
                    getStatus(label.meta, validating) === 'error' && label.meta.error
                  }
                >
                  <Select
                    placeholder={multiFormatMessage('task_label_placeholder')}
                    size="full"
                    appearance="button"
                    searchable={!showCustomLabelInput}
                    matchButtonWidth
                    value={(label.input.value && originTaskId) ? label.input.value : undefined}
                    onChange={(value) => {
                      form.change('label', value);
                    }}
                    options={
                      !showCustomLabelInput
                        ? labelOptions.map((opt) => ({
                          value: opt.value,
                          text: isEn ? opt.text_en : opt.text,
                        }))
                        : []
                    }
                    boxClassName={showCustomLabelInput ? 'confirm-model-customize' : ''}
                    tips={
                      // 自定义输入模式下不展示 StatusTip；无选项时才展示 loading/empty/error
                      // eslint-disable-next-line no-nested-ternary
                      showCustomLabelInput ? false : labelStatus && labelOptions.length === 0 ? (
                        <StatusTip
                          status={labelStatus}
                          emptyText={commonFormatMessage('no_data')}
                          onRetry={() => {
                            runGetChartTagList();
                          }}
                        />
                      ) : undefined
                    }
                    onOpen={() => {
                      setShowCustomLabelInput(false);
                    }}
                    footer={
                      !showCustomLabelInput ? (
                        <div
                          style={{ color: '#0076ff', textAlign: 'left', cursor: 'pointer' }}
                          onClick={() => {
                            setShowCustomLabelInput(true);
                          }}
                        >
                          {multiFormatMessage('task_custom')}
                        </div>
                      ) : (
                        <>
                          <Input
                            size="full"
                            placeholder={multiFormatMessage('task_custom_placeholder')}
                            value={customLabelInputValue}
                            onChange={(value) => {
                              setCustomLabelInputValue(value);
                            }}
                          />
                          <div className="confirm-flex-end" style={{ marginTop: '10px' }}>
                            <Button
                              style={{ minWidth: 50, padding: '0px 15px' }}
                              type="primary"
                              disabled={!customLabelInputValue}
                              onClick={() => {
                                runCreateChartTag(customLabelInputValue);
                              }}
                            >
                              {commonFormatMessage('confirm')}
                            </Button>
                            <Button
                              style={{ minWidth: 50, padding: '0px 15px' }}
                              type="weak"
                              onClick={() => {
                                setShowCustomLabelInput(false);
                              }}
                            >
                              {commonFormatMessage('return')}
                            </Button>
                          </div>
                        </>
                      )
                    }
                  />
                </Form.Item>
              </Form>
            </Modal.Body>
            <Modal.Footer>
              <Button type="primary" htmlType="submit" loading={submitting} disabled={invalid}>
                {commonFormatMessage('confirm')}
              </Button>
              <Button type="weak" onClick={onClose}>
                {commonFormatMessage('cancel')}
              </Button>
            </Modal.Footer>
          </form>
        </ConfigProvider>
      </Loading>
    </Modal>
  );
};

export default CopyChartModal;
